import styled, { css } from 'styled-components';

import { pickColor } from 'themes/helpers/pickColor';
import { mediaMinWidth } from 'themes/mixins/mediaQueries';

import {
  CODE_COLUMN_WIDTH,
  COLUMN_GAP,
  FIELD_BOX_HEIGHT,
  TOGGLE_COLUMN_WIDTH,
  boxBase,
} from 'components/OrderAddressBlocks/layout';
import { SelectWithControl } from 'components/SelectWithControl';
import { TextArea } from 'components/TextArea';

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.2rem;

  padding-bottom: 4.8rem;

  border-bottom: 0.1rem solid ${pickColor('gray')};

  ${mediaMinWidth.md(css`
    flex-direction: column;
  `)};
`;

const PrimaryStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  padding-bottom: 3.2rem;
`;

const CompactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1.6rem 2.4rem;
  padding-bottom: 4.8rem;
  margin-left: calc(${TOGGLE_COLUMN_WIDTH} + ${COLUMN_GAP});
`;

const CompactCellReadonly = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex: 0 1 ${CODE_COLUMN_WIDTH};
  min-width: 12rem;
`;

const CompactCellSelect = styled.div`
  flex: 1 1 14rem;
  min-width: 12rem;
  max-width: 25.7rem;
  margin-left: auto;
`;

const CompactCellCheckbox = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding-bottom: 0.4rem;
`;

const Select = styled(SelectWithControl)`
  text-transform: uppercase;
  min-width: 20rem;
  flex: 1;
`;

const CompactSelect = styled(SelectWithControl)`
  text-transform: uppercase;
  min-width: 0;
  width: 100%;
`;

const NotesStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  padding-bottom: 3.2rem;
`;

/** Notes textarea — one line, or full height when expanded; `$hidden` keeps only the label. */
const NoteArea = styled(TextArea)<{
  $expanded: boolean;
  $invalid?: boolean;
  $hidden?: boolean;
}>`
  width: 100%;
  font-size: 1.2rem;
  text-transform: uppercase;

  textarea {
    font-size: 1.4rem;
    line-height: 1.5;
    min-height: ${FIELD_BOX_HEIGHT};
    height: ${({ $expanded }) => ($expanded ? '12rem' : FIELD_BOX_HEIGHT)};
    overflow-y: auto;
    transition: height 300ms ease;

    /* Kept mounted (registered with the form) but not shown. */
    ${({ $hidden }) =>
      $hidden &&
      css`
        display: none;
      `};

    ${({ $invalid }) =>
      $invalid &&
      css`
        border-color: ${pickColor('red')};
      `};
  }
`;

const NoteField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
`;

/** Length counter under a note; red once the note is over the limit. */
const NoteCounter = styled.div<{ $over: boolean }>`
  text-align: right;
  font-family: 'LFT Zeno', sans-serif;
  font-size: 1.2rem;
  font-weight: 400;
  color: ${({ $over }) => ($over ? pickColor('red') : pickColor('grayDark'))};
`;

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;

  label {
    font-weight: 600;
    font-family: 'LFT Zeno', sans-serif;
    font-size: 1.2rem;
    color: ${pickColor('black')};
    cursor: pointer;
  }

  input[type='checkbox'] {
    width: 1.6rem;
    height: 1.6rem;
    cursor: pointer;
  }
`;

const ReadOnlyField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 20rem;
  flex: 1;
`;

const ReadOnlyLabel = styled.span`
  font-weight: 600;
  font-family: 'LFT Zeno', sans-serif;
  font-size: 1.2rem;
  color: ${pickColor('black')};
  text-transform: uppercase;
`;

const ReadOnlyValue = styled.span`
  font-family: 'LFT Zeno', sans-serif;
  font-size: 1.4rem;
  color: ${pickColor('grayDark')};
  text-transform: uppercase;
`;

/** Read-only customer field — label is uppercase like the select labels. */
const ReadOnlyCodeField = styled.div`
  text-transform: uppercase;
`;

/** Read-only code box — same footprint as the select it replaces. */
const CodeBox = styled.div`
  ${boxBase};
  text-transform: uppercase;
`;

/** Read-only `Season - Collection` value, boxed and bold as in the mockup. */
const SeasonCollectionBox = styled.div`
  ${boxBase};
  font-weight: 700;
  text-transform: uppercase;
`;

const FieldWithDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FormSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

/** Skeleton rows shown for the moment before the product table mounts. */
const ProductTableFallback = styled.div`
  width: 100%;
`;

export {
  CheckboxField,
  CodeBox,
  CompactCellCheckbox,
  CompactCellReadonly,
  CompactCellSelect,
  CompactRow,
  CompactSelect,
  FieldWithDetails,
  FormSection,
  NoteArea,
  NoteCounter,
  NoteField,
  NotesStack,
  PrimaryStack,
  ProductTableFallback,
  ReadOnlyCodeField,
  ReadOnlyField,
  ReadOnlyLabel,
  ReadOnlyValue,
  SeasonCollectionBox,
  Select,
  Wrapper,
};
